import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { X } from "lucide-react";
import SEO from "@/components/SEO";

const GalleryPage = ({ seoSettings }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 lg:pt-[118px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-[118px] min-h-screen">
      <SEO 
        title={`Gallery - ${seoSettings?.seo_title || 'Mélange Mobile Bar'}`}
        description={seoSettings?.seo_description}
        keywords={seoSettings?.seo_keywords}
        ogImage={seoSettings?.seo_og_image}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F7F4F0] to-[#F7F4F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="gallery-page-title">
            Gallery
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Take a look at some of our past events and creations
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {images.map((image, index) => (
              <div
                key={image.id}
                data-testid={`gallery-image-${index}`}
                onClick={() => setSelectedImage(image)}
                className="aspect-square bg-gradient-to-br from-[#F0EBE3] to-[#F0EBE3]-200 rounded-lg overflow-hidden cursor-pointer hover-lift shadow-md"
              >
                <div className="w-full h-full flex items-center justify-center text-6xl">
                  🍸
                </div>
              </div>
            ))}
          </div>

          {images.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-600">No images in gallery yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox-modal"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
            data-testid="close-lightbox-button"
          >
            <X size={32} />
          </button>
          <div className="max-w-4xl w-full bg-white rounded-lg p-8">
            <div className="aspect-video bg-gradient-to-br from-[#F0EBE3] to-[#F0EBE3]-200 rounded-lg flex items-center justify-center mb-4">
              <div className="text-9xl">🍹</div>
            </div>
            {selectedImage.title && (
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedImage.title}</h3>
            )}
            {selectedImage.description && (
              <p className="text-gray-600">{selectedImage.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
